// session.js
// Central handling for an expired/rejected auth token. Before this the webapp had
// no expiry handling at all: userData was read from localStorage once at load and
// a 401 from any service was simply swallowed, so an iPad left running would keep
// showing a logged-in screen while every request quietly failed.
import { forceLogout, isSessionValid } from "./auth";

// How often the watchdog re-checks validTill
const EXPIRY_CHECK_INTERVAL_MS = 60 * 1000;

// Only a 401 means "this token is not valid". The server answers 503 (with
// Retry-After) when it could not check the token because of a database/infra
// problem - that says nothing about the session, so it must never log anyone out.
export const isSessionEndingStatus = (status) => status === 401;

// Reconnect delay for the live-events stream after an error. Backs off
// exponentially (30s, 1m, 2m ... capped at 5m) with a little jitter so that
// every iPad does not hit a recovering server in the same second, which is
// exactly what turned a short database blip into a wave of failed token checks.
export const SSE_RETRY_BASE_MS = 30 * 1000;
export const SSE_RETRY_MAX_MS = 5 * 60 * 1000;
export const nextRetryDelayMs = (attempt, random = Math.random) => {
  const ceiling = Math.min(SSE_RETRY_MAX_MS, SSE_RETRY_BASE_MS * Math.pow(2, attempt));
  return Math.round(ceiling * (0.8 + 0.2 * random()));
};

// Requests that are expected to run while logged out - never treat their
// failures as an expired session.
const AUTH_FREE_PATHS = [
  "operatorLogin",
  "forgotpassword",
  "resetpassword",
  "logout"
];

const isAuthFreeRequest = (url) => {
  if (!url) return false;
  const target = String(url).toLowerCase();
  return AUTH_FREE_PATHS.some((path) => target.includes(path.toLowerCase()));
};

// Only end the session for a 401 that happened on a call we made as a logged-in
// operator. Anything else (login failures, network errors) is left alone.
export const handleUnauthorized = (url) => {
  if (isAuthFreeRequest(url)) return;
  if (localStorage.getItem("isLogged") !== "true") return;
  forceLogout(`401 from ${url || "server"}`);
};

let installed = false;

// Installed once from index.js. Covers the three ways this app talks to the
// backend: axios (default instance), window.fetch, and the validTill watchdog.
export const installSessionGuards = (axios) => {
  if (installed) return;
  installed = true;

  // 1. axios - every call except LoginForm's own instance goes through the default one
  if (axios) {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error && error.response && isSessionEndingStatus(error.response.status)) {
          handleUnauthorized(error.config && error.config.url);
        }
        return Promise.reject(error);
      }
    );
  }

  // 2. fetch - patched centrally rather than at the ~15 call sites that use it
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (isSessionEndingStatus(response.status)) {
      const [input] = args;
      const url = typeof input === "string" ? input : input && input.url;
      handleUnauthorized(url);
    }
    return response;
  };

  // 3. validTill watchdog - catches expiry even when the iPad is idle and no
  // request has been made to surface a 401
  setInterval(() => {
    if (localStorage.getItem("isLogged") !== "true") return; // not logged in, nothing to expire
    if (!isSessionValid()) {
      forceLogout("token validTill elapsed");
    }
  }, EXPIRY_CHECK_INTERVAL_MS);
};
